import {
  AhaEditorUtils,
  AllPlugins,
  RichTextPlugins,
  UiPlugins,
  WritingAssistantProvider,
  type ReferenceAhaObject,
  type AhaEditorV2Controller,
  type DocumentValue,
  type EditorPlugin,
  type BackwardsCompatibleDocumentValue,
} from '@aha-app/aha-editor-v2';
import ApplicationController from '@aha-app/mvc';
import attachmentPasteTransformerFactory from 'components/aha_editor_field_v2/plugins/Attachments/AttachmentPasteTransformerFactory';
import { EditorTemplateSelectionPluginFactory } from 'components/aha_editor_field_v2/plugins/EditorTemplateSelectionPlugin/EditorTemplateSelectionPlugin';
import HiddenFormInputPluginFactory, {
  type HiddenFormInputOptions,
} from 'components/aha_editor_field_v2/plugins/HiddenFormInput/HiddenFormInputPlugin';
import DraftManagerPluginFactory from 'components/aha_editor_field_v2/plugins/LayoutComponents/DraftManagerPlugin';
import FullScreenTopBarPluginFactory, {
  type FullScreenProviderProps,
} from 'components/aha_editor_field_v2/plugins/LayoutComponents/FullScreenTopBar';
import AhaMediaProvider from 'components/aha_editor_field_v2/plugins/MediaProvider/AhaMediaProvider';
import ReferenceAhaPlugin from 'components/aha_editor_field_v2/plugins/ReferenceAha/ReferenceAhaPlugin';
import ReferenceUserPlugin from 'components/aha_editor_field_v2/plugins/ReferenceUser/ReferenceUserPlugin';
import BatchedRecordLoader from 'components/framework/BatchedRecordLoader';
import type { InsertTemplateConfiguration } from 'components/aha_editor_field_v2/controllers/AhaEditorFieldV2Controller';
import { SidePanelPluginFactory } from 'components/aha_editor_field_v2/plugins/SidepanelPlugin/SidePanelPlugin';
import type { SidePanelItem } from 'javascripts/shared-types';
import SetEditorControllerPlugin from 'components/aha_editor_field_v2/plugins/SetEditorController/SetEditorControllerPlugin';
import AssistedEditor from 'components/Assistant/agents/WritingAgent/AssistedEditor';
import { assistantSidePanelConfiguration } from 'components/Assistant';
import SubmitFormPlugin from 'components/aha_editor_field_v2/plugins/SubmitFormPlugin/SubmitFormPlugin';
import AhaDocumentTemplatePluginFactory, {
  AHA_DOCUMENT_TEMPLATE_PLUGIN_ID,
  type AhaDocumentTemplatePlugin,
} from 'components/aha_editor_field_v2/plugins/InsertTemplate/AhaDocumentTemplatePlugin';
import {
  BEST_PRACTICES_PANEL_KEY,
  BestPracticesSidebarItem,
} from 'components/whiteboards/best_practices_panel/BestPracticesPanel';
import ValueChangeCallbackPlugin from 'components/aha_editor_field_v2/plugins/ValueChangeCallback/ValueChangeCallbackPlugin';

export interface AhaNewRecordEditorControllerProps
  extends FullScreenProviderProps {
  name?: string;
  body?: string;
  className?: string;
  inputFormat?: HiddenFormInputOptions['format'];
  projectId?: RecordId;
  objectType?: string;
  enclosingSelector?: string;
  templateTarget?: string;
  saveDrafts?: boolean;
  attachmentPath?: string;
  disableAttachments?: boolean;
  disableAddingAttachments?: boolean;
  placeholderElement?: string;
  supportsAssistant?: boolean;
  supportsMentions?: boolean;
  supportsReferences?: boolean;
  supportsTemplates?: boolean;
  watchableProjectId?: RecordId;
  watchCallbackSelector?: string;
  showAvatar?: boolean;
  linkType?: string;
  linkURL?: string;
  value?: BackwardsCompatibleDocumentValue | null;
  focusOnMount?: boolean;
  searchURL?: string;
  watchURL?: string;
  tableOfContentsEnabled?: boolean;
  usedTemplateIds?: string[];
  draftStorageKey?: string;

  // Used by React components to handle changes to document value
  onChange?: (value: DocumentValue) => void;
  onChangeHTMLValue?: (html: string) => void;
}

interface AhaNewRecordEditorControllerState {}

class AhaNewRecordEditorController extends ApplicationController<
  AhaNewRecordEditorControllerState,
  AhaNewRecordEditorControllerProps
> {
  editorController: AhaEditorV2Controller;
  renderValue: DocumentValue;
  plugins: EditorPlugin[] = [];
  container: HTMLElement;
  insertTemplateConfig?: InsertTemplateConfiguration;

  async initialize(props: AhaNewRecordEditorControllerProps) {
    const { body, value } = props;

    this.renderValue = AhaEditorUtils.Editor.initializeValue(value, body);

    this.initializePlugins();
    this.initializeInsertTemplates();
  }

  onEditorControllerReady = (editorController: AhaEditorV2Controller) => {
    this.editorController = editorController;

    this.initializeSidePanels();
  };

  initializePlugins() {
    this.plugins = [...RichTextPlugins];

    const { onChange, onChangeHTMLValue } = this.props;
    if (onChange) {
      this.plugins.push(
        ValueChangeCallbackPlugin({
          format: 'json',
          onChange,
        })
      );
    }

    if (onChangeHTMLValue) {
      this.plugins.push(
        ValueChangeCallbackPlugin({
          format: 'html',
          onChange: onChangeHTMLValue,
        })
      );
    }

    this.plugins.push(UiPlugins.SmoothScrollingPlugin({ top: 50, bottom: 60 }));

    this.plugins.push(SubmitFormPlugin);

    this.plugins.push(
      SetEditorControllerPlugin({
        setController: this.onEditorControllerReady,
      })
    );

    if (this.props.supportsFullscreen) {
      this.plugins.push(
        FullScreenTopBarPluginFactory({
          getContainer: () => this.container,
          getEnclosingElement: () => this.enclosingElement,
          fullScreenURL: this.props.fullScreenURL,
          nonFullScreenURL: this.props.nonFullScreenURL,
        })
      );
    }

    if (this.props.name) {
      this.plugins.push(
        HiddenFormInputPluginFactory({
          name: this.props.name,
          format: this.props.inputFormat || 'json',
        })
      );

      if (this.props.saveDrafts) {
        this.plugins.push(
          DraftManagerPluginFactory({
            keyPrefix: this.props.name,
            keyValue: this.props.draftStorageKey,
          })
        );
      }
    }

    if (this.props.supportsMentions) {
      this.plugins.unshift(
        ...ReferenceUserPlugin({
          projectId: this.props.watchableProjectId,
          watchURL: this.props.watchURL,
          watchCallbackSelector: this.props.watchCallbackSelector,
          searchURL: this.props.searchURL,
          showAvatar: this.props.showAvatar,
        })
      );
    }

    if (this.props.supportsReferences) {
      this.plugins.unshift(
        ...ReferenceAhaPlugin({
          onInsertReference: this.actionCreateRecordLinks.bind(this),
          isModifiable: () => true,
          batchedRecordLoader: new BatchedRecordLoader(),
        })
      );
    }

    if (
      this.props.name &&
      this.props.objectType &&
      !this.props.disableAddingAttachments &&
      !this.props.disableAttachments
    ) {
      const attachmentPasteTransformerOptions = {
        namePrefix: this.props.name.split('[editor_content]')[0],
        objectType: this.props.objectType,
        getEnclosingElement: () => this.enclosingElement,
      };

      (['image', 'video', 'audio', 'attachment'] as const).forEach(type => {
        const pasteTransformerPlugin = attachmentPasteTransformerFactory({
          type,
          ...attachmentPasteTransformerOptions,
        });
        this.plugins.push(pasteTransformerPlugin);
      });
    }

    if (!this.props.disableAttachments && this.props.attachmentPath) {
      this.plugins.push({
        name: 'Aha Editor Field MediaProvider',
        provider: new AhaMediaProvider({
          attachmentPath: this.props.attachmentPath,
          getEnclosingElement: () => this.enclosingElement,
          hasSharedPath: false,
        }),
      });

      // Must be before the link insertData plugin
      this.plugins.unshift(...AllPlugins.EmbedPlugin);
    }

    if (this.props.supportsAssistant && this.props.supportsFullscreen) {
      this.plugins.push({
        name: 'Aha! Editor Field WritingAssistantProvider',
        provider: new WritingAssistantProvider({
          onOpenWritingAssistant: () => {
            this.actionShowSidePanel({
              panel: 'writing-assistant',
              open: true,
            });
          },
        }),
      });
    }

    if (this.props.templateTarget) {
      this.plugins.push(
        EditorTemplateSelectionPluginFactory({
          target: this.props.templateTarget,
        })
      );
    }

    this.plugins.push(SidePanelPluginFactory({ objectType: 'Note' }));

    if (this.props.tableOfContentsEnabled) {
      this.plugins.push(AllPlugins.TableOfContentsPlugin);

      // In v1, this was also controlled by
      // tableOfContentsEnabled. For consistency, we'll do the same
      // here. Eventually we plan to support these plugins everywhere.
      this.plugins.push(
        ...[AllPlugins.LabelPillPlugin, AllPlugins.InfoBlockPlugin].flat(10)
      );
    }
  }

  initializeInsertTemplates() {
    if (!this.props.supportsTemplates || !this.props.objectType) return;

    this.insertTemplateConfig = {
      projectId: this.props.projectId,
      showWhiteboardTemplates: false,
      objectType: this.props.objectType,
    };

    this.plugins.push(
      AhaDocumentTemplatePluginFactory({
        controller: this,
        usedTemplateIds: this.props.usedTemplateIds || [],
      })
    );
  }

  initializeSidePanels() {
    const sidePanels: SidePanelItem[] = [];

    if (
      this.props.supportsAssistant &&
      this.props.supportsFullscreen &&
      this.editorController
    ) {
      const assistedEditor = new AssistedEditor(this.editorController);
      assistedEditor.registerSelectionChangePlugin();

      const assistantSidePanel = assistantSidePanelConfiguration({
        agentName: 'WritingAgent',
        agentParams: {
          projectId: this.props.projectId,
        },
        agentInstances: {
          assistedEditor,
        },
      });
      if (assistantSidePanel) sidePanels.push(assistantSidePanel);
    }

    const templatePlugin: AhaDocumentTemplatePlugin | undefined =
      this.ahaEditor?.getPlugin(AHA_DOCUMENT_TEMPLATE_PLUGIN_ID);

    const usedTemplateIds = templatePlugin?.api.getUsedTemplateIds() || [];
    if (usedTemplateIds.length > 0) {
      sidePanels.push(
        BestPracticesSidebarItem(usedTemplateIds, 'note', content => {
          const templatePlugin: AhaDocumentTemplatePlugin | undefined =
            this.ahaEditor?.getPlugin(AHA_DOCUMENT_TEMPLATE_PLUGIN_ID);

          if (templatePlugin && this.ahaEditor) {
            templatePlugin.api.insertExampleContent(
              this.ahaEditor.editor,
              content
            );
            this.actionShowSidePanel({
              open: true,
              panel: BEST_PRACTICES_PANEL_KEY,
            });
          }
        })
      );
    }

    const sidePanelProvider = this.ahaEditor?.getProvider('sidePanel');
    sidePanelProvider?.setSidePanels(sidePanels);
  }

  actionShowSidePanel(
    options: { open: false } | { open: true; panel: string } = { open: false }
  ) {
    const sidePanelProvider = this.ahaEditor?.getProvider('sidePanel');

    if (options.open) {
      sidePanelProvider?.onOpenSidePanel(options.panel);
    } else {
      sidePanelProvider?.onCloseSidePanel();
    }
  }

  get editorState() {
    if (!this.editorController) {
      return;
    }

    return this.editorController.state;
  }

  get pluginState() {
    return this.editorState?.pluginState;
  }

  get ahaEditor() {
    if (!this.editorController) {
      return;
    }

    return this.editorController.ahaEditor;
  }

  setContainer(container: HTMLElement) {
    this.container = container;
  }

  get allowedMediaRegex() {
    return new RegExp(
      `^${window.location.protocol}//${window.location.host}/(develop/|empathy/)?attachments/\\d+/token/[0-9a-f]+\\.download`
    );
  }

  get enclosingElement() {
    if (!this.container || !this.props.enclosingSelector) return;

    const el = this.container.closest<HTMLElement>(
      this.props.enclosingSelector
    );

    if (el) {
      return el;
    }
  }

  actionCreateRecordLinks(reference: ReferenceAhaObject) {
    if (!this.props.linkURL) return;

    if (['Competitor', 'Persona'].includes(reference.class)) {
      return;
    }

    $.ajax({
      url: this.props.linkURL,
      type: 'POST',
      data: {
        record_link: {
          record_ids: reference.id,
          record_reference_nums: reference.reference_num, // NOTE: This is added for convert-on-paste reference links where we don't have the record id
          record_type: reference.class,
          link_type: this.props.linkType,
        },
      },
    });
  }
}

export default AhaNewRecordEditorController;
